// PowerBiViewsModal — marks which forms have a Power BI view published.
//
// Two reads, joined on formId:
//   · GET exports/enabled-vertical-export-forms → the CANDIDATES (forms whose
//     vertical export is live; nothing else can back a view)
//   · the powerbi_export item of GET exports/list → the MARKED set, its `forms`
//
// The save REPLACES the whole set (`{ tableName, forms }` carries every marked
// form, not a delta), which is why ticking is a local draft committed once by
// Save. Cancel closes without committing, and since the draft is re-seeded from
// server state on every open, a cancelled session leaves nothing behind.
//
// Search filters in memory: the endpoint takes no params and returns everything
// at once, so there is no pagination to misrepresent and no request to debounce
// (the opposite call from the page's own search, which is server-side because
// that list IS paged).
//
// Rows are the shared Checkbox with a two-line `label` (title over table name)
// — `label` is a ReactNode, so it holds both. `className` lands on Checkbox's
// root <label>, so the row keeps its own block name and stays click-to-toggle,
// and its styling is scoped in the page partial (the MultiSectionCheckboxDropdown
// pattern) rather than copying app-checkbox's internals into this file.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Checkbox, Input, Modal } from '@/components/common';
import {
  SqlExportType,
  useAddPowerBiExportMutation,
  useGetCompanyExportsQuery,
  useGetEnabledVerticalFormsQuery,
  type ExportFormRef,
} from '@/api/endpoints/dataSetupApi';
import type { ApiErrorShape } from '@/api/types';
import { useAppSelector } from '@/store/hooks';
import { selectCompanyDetail } from '@/redux/auth/selectors';
import { POWER_BI_TABLE_PREFIX, companyTableName } from './companyExportSections';

interface PowerBiViewsModalProps {
  open: boolean;
  onClose: () => void;
}

const PowerBiViewsModal = ({ open, onClose }: PowerBiViewsModalProps) => {
  const { t } = useTranslation();
  const companyCode = useAppSelector(selectCompanyDetail)?.companyCode;
  const tableName = companyTableName(POWER_BI_TABLE_PREFIX, companyCode);

  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<Set<string>>(new Set());

  // Both skipped while closed, so opening the modal is what fetches.
  const { data: forms = [], isLoading } = useGetEnabledVerticalFormsQuery(undefined, {
    skip: !open,
  });
  const { data: exports = [], isSuccess: exportsReady } = useGetCompanyExportsQuery(undefined, {
    skip: !open,
  });
  const [addPowerBiExport, { isLoading: saving }] = useAddPowerBiExportMutation();

  const powerBi = exports.find((e) => e.type === SqlExportType.PowerBi) ?? null;
  /** Both reads in. The candidates give the rows, the exports give the ticks. */
  const ready = exportsReady && !isLoading;

  // Seed from server state on open, and AGAIN when the query resolves: both
  // reads are skipped while closed, so at the instant `open` flips there is
  // nothing to copy and the modal would show every box unticked.
  //
  // Depend on `powerBi`, never on `exports` — `exports` is `data ?? []`, so
  // while the query is unresolved that fallback mints a new array identity
  // every render, and this effect would re-run forever (each `new Set` is a
  // new identity too, so it never reaches a fixed point). `powerBi` is an
  // element out of RTK Query's cached array, so its identity is stable.
  //
  // Tradeoff: anything that invalidates COMPANY_EXPORTS while this modal is
  // open would reset the user's ticks mid-edit. Unreachable today — the only
  // invalidators are the drawer's mutations and this modal's own save, none of
  // which can fire while it is open, and refetchOnFocus/OnReconnect are at
  // their false defaults. If a background refetch is ever added to that tag,
  // switch to `touched ?? marked` (draft null until first toggle) instead.
  useEffect(() => {
    if (open) {
      setDraft(new Set(powerBi?.forms.map((f) => f.formId) ?? []));
      setSearch('');
    }
  }, [open, powerBi]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? forms.filter((f) => f.formTitle.toLowerCase().includes(q)) : forms;
  }, [forms, search]);

  const toggle = (formId: string) =>
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(formId)) next.delete(formId);
      else next.add(formId);
      return next;
    });

  const save = async () => {
    // The payload wants both fields per form, and the candidate rows carry
    // both — so the marked set is built straight from them, no second lookup.
    const marked: ExportFormRef[] = forms
      .filter((f) => draft.has(f.formId))
      .map((f) => ({ formId: f.formId, verticalTableName: f.verticalTableName }));
    try {
      await addPowerBiExport({ tableName, forms: marked }).unwrap();
      toast.success(t('data_setup_powerbi_saved'));
      onClose();
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className='powerbi-views'
      title={t('data_setup_powerbi_title')}
      subtitle={t('data_setup_powerbi_subtitle', { marked: draft.size, total: forms.length })}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          {/* The payload REPLACES the whole set, so saving before the current
              set is known — still loading, or the query errored — would wipe
              it. Hence exportsReady, not just a spinner. */}
          <Button
            iconStart='check'
            onClick={save}
            loading={saving}
            disabled={!tableName || !exportsReady}
          >
            {t('button_save')}
          </Button>
        </>
      }
    >
      <div className='powerbi-views__body'>
        {tableName ? (
          <>
            <Input
              size='sm'
              iconStart='search'
              placeholder={t('data_setup_powerbi_search_ph')}
              aria-label={t('field_search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <div className='powerbi-views__list'>
              {/* Wait for BOTH reads before drawing rows: the candidates give
                  the list, the exports give the ticks, and rendering on the
                  first alone shows everything unticked — which reads as
                  "nothing is marked" rather than "not loaded yet". */}
              {!ready && <p className='powerbi-views__empty'>{t('text_loading')}</p>}

              {ready &&
                shown.map((f) => (
                  <Checkbox
                    key={f.formId}
                    className='powerbi-views__row'
                    checked={draft.has(f.formId)}
                    onChange={() => toggle(f.formId)}
                    label={
                      <>
                        <span className='powerbi-views__row-name'>{f.formTitle}</span>
                        {/* SQL identifiers stay latin, including under Arabic. */}
                        <code className='powerbi-views__row-sub' dir='ltr'>
                          {f.verticalTableName}
                        </code>
                      </>
                    }
                  />
                ))}

              {ready && shown.length === 0 && (
                <p className='powerbi-views__empty'>
                  {forms.length === 0 ? t('data_setup_powerbi_empty') : t('text_no_results_found')}
                </p>
              )}
            </div>
          </>
        ) : (
          // No company code means no derivable table name, so there is nothing
          // to save against — the same guard the drawer's sections use.
          <p className='powerbi-views__empty'>{t('data_setup_sql_no_company_code')}</p>
        )}
      </div>
    </Modal>
  );
};

export default PowerBiViewsModal;
